// src/components/team/Territories.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import {
  MapPinned,
  Search,
  Plus,
  Edit2,
  Eye,
  CheckCircle,
  Trash2,
  Layers,
  Users as UsersIcon,
  Sparkles,
  X,
  CheckSquare2,
  RefreshCw,
} from "lucide-react";

// ✅ NEW: notify user when they are assigned to a territory
import { notifyTerritoryAssigned } from "../../utils/notifications";

// -----------------------------
// Helpers
// -----------------------------
const normalizeRole = (role) => (role || "").toString().trim().toLowerCase();

const isStaffRole = (role) => {
  const r = normalizeRole(role);
  return r === "owner" || r === "admin" || r === "enduser";
};

const getCompanyId = (me) =>
  me?.companyId  || null;

const getOrgContext = (me) => {
  const companyId = getCompanyId(me);
  if (!companyId) return null;
  return { root: "companies", id: companyId };
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

const getUserId = (u) => u?.id || u?.uid || null;
const getUserLabel = (u) =>
  (u?.fullName || u?.displayName || u?.email || "User").toString().trim();

const getInitials = (name, email) => {
  const n = (name || "").toString().trim();
  if (n) {
    const parts = n.split(" ").filter(Boolean);
    if (parts.length === 1) return (parts[0].slice(0, 2) || "U").toUpperCase();
    return ((parts[0][0] || "U") + (parts[parts.length - 1][0] || "U")).toUpperCase();
  }
  const e = (email || "").toString().trim();
  return e ? e[0].toUpperCase() : "U";
};

const getUserTerritoryIds = (user) =>
  safeArray(user?.territoryIds || user?.territories || []).filter(Boolean);

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const setEquals = (aArr, bArr) => {
  const a = new Set((aArr || []).filter(Boolean));
  const b = new Set((bArr || []).filter(Boolean));
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

const norm5 = (z) => String(z || "").trim().slice(0, 5);
const isUSPS = (s) => /^[A-Z]{2}$/.test(String(s || "").trim());

// State name -> USPS
const NAME_TO_USPS = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  "DISTRICT OF COLUMBIA": "DC",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
};

// Accept "Florida" or "FL"
const stateToCode = (v) => {
  const raw = String(v || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  return NAME_TO_USPS[raw] || raw;
};

const parseStates = (input) => {
  const parts = String(input || "")
    .split(",")
    .map((s) => stateToCode(s))
    .map((s) => String(s || "").trim().toUpperCase())
    .filter(Boolean);
  const codes = uniq(parts).filter(isUSPS);
  return codes;
};

const parseCities = (input) => {
  const parts = String(input || "")
    .split(",")
    .map((c) => String(c || "").trim())
    .filter(Boolean);

  // de-dupe case-insensitively while preserving casing of first occurrence
  const seen = new Set();
  const out = [];
  for (const c of parts) {
    const k = c.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
};

const parseZips = (input) => {
  const parts = String(input || "")
    .split(",")
    .map((z) => norm5(z))
    .map((z) => z.replace(/[^\d]/g, ""))
    .filter((z) => z.length === 5);
  return uniq(parts);
};

const TERRITORY_PACKS = [
  {
    id: "national_foodservice",
    name: "National Foodservice Regions",
    description: "A broad regional split for distributor, rep, and sample coverage.",
    territories: [
      { name: "Northeast", states: ["ME", "NH", "VT", "MA", "RI", "CT", "NY", "NJ", "PA"] },
      { name: "Mid-Atlantic", states: ["DE", "MD", "DC", "VA", "WV"] },
      { name: "Southeast", states: ["NC", "SC", "GA", "FL", "AL", "MS", "TN"] },
      { name: "Great Lakes", states: ["OH", "MI", "IN", "IL", "WI", "MN"] },
      { name: "South Central", states: ["KY", "AR", "LA", "OK", "TX"] },
      { name: "Mountain West", states: ["CO", "UT", "ID", "MT", "WY", "NM", "AZ", "NV"] },
      { name: "West Coast", states: ["CA", "OR", "WA", "AK", "HI"] },
    ],
  },
  {
    id: "coastal_resort",
    name: "Coastal & Resort Coverage",
    description: "Helpful for hospitality-heavy menus, seafood programs, and resort accounts.",
    territories: [
      { name: "Atlantic Coast", states: ["FL", "GA", "SC", "NC", "VA", "MD", "DE", "NJ", "NY", "CT", "RI", "MA", "NH", "ME"] },
      { name: "Gulf Coast", states: ["TX", "LA", "MS", "AL", "FL"] },
      { name: "Great Lakes Shoreline", states: ["MN", "WI", "IL", "IN", "MI", "OH", "PA", "NY"] },
      { name: "Mountain Resorts", states: ["CO", "UT", "ID", "MT", "WY", "NM"] },
      { name: "Pacific Coast", states: ["CA", "OR", "WA", "AK", "HI"] },
    ],
  },
  {
    id: "institutional_mix",
    name: "Campus & Healthcare Coverage",
    description: "A practical split for schools, campuses, healthcare, and senior living operators.",
    territories: [
      { name: "Campus East", states: ["MA", "RI", "CT", "NY", "NJ", "PA", "MD", "VA", "NC"] },
      { name: "Campus Central", states: ["OH", "MI", "IN", "IL", "WI", "MN", "IA", "MO", "KS", "NE"] },
      { name: "Campus South & West", states: ["SC", "GA", "FL", "TN", "KY", "AR", "LA", "OK", "TX", "CO", "AZ", "CA", "OR", "WA"] },
      { name: "Healthcare East", states: ["ME", "NH", "VT", "MA", "RI", "CT", "NY", "NJ", "PA", "DE", "MD", "DC", "VA", "WV", "NC", "SC", "GA", "FL"] },
      { name: "Healthcare Central & West", states: ["OH", "MI", "IN", "IL", "WI", "MN", "IA", "MO", "KS", "NE", "ND", "SD", "TX", "NM", "CO", "WY", "MT", "ID", "UT", "AZ", "NV", "CA", "OR", "WA", "AK", "HI"] },
    ],
  },
  {
    id: "major_city_hubs",
    name: "Major City Hubs",
    description: "City-first territories for metro-heavy foodservice coverage and rep lanes.",
    territories: [
      { name: "NYC Metro", states: ["NY", "NJ", "CT"], cities: ["New York", "Brooklyn", "Queens", "Bronx", "Jersey City", "Newark", "Stamford"] },
      { name: "South Florida", states: ["FL"], cities: ["Miami", "Fort Lauderdale", "West Palm Beach"] },
      { name: "Chicago Metro", states: ["IL", "IN", "WI"], cities: ["Chicago", "Evanston", "Naperville", "Gary", "Milwaukee"] },
      { name: "Dallas–Fort Worth", states: ["TX"], cities: ["Dallas", "Fort Worth", "Plano", "Irving", "Arlington"] },
      { name: "Southern California", states: ["CA"], cities: ["Los Angeles", "Long Beach", "Anaheim", "Irvine", "San Diego"] },
      { name: "Bay Area", states: ["CA"], cities: ["San Francisco", "Oakland", "San Jose", "Berkeley"] },
    ],
  },
  {
    id: "regional_city_clusters",
    name: "Regional City Clusters",
    description: "Useful for operators organized around restaurant city clusters instead of whole states.",
    territories: [
      { name: "Carolinas Cities", states: ["NC", "SC"], cities: ["Charlotte", "Raleigh", "Durham", "Greensboro", "Charleston", "Columbia"] },
      { name: "Mid-Atlantic Cities", states: ["VA", "MD", "DC", "PA"], cities: ["Richmond", "Norfolk", "Baltimore", "Washington", "Philadelphia", "Pittsburgh"] },
      { name: "Texas Triangle", states: ["TX"], cities: ["Dallas", "Fort Worth", "Austin", "San Antonio", "Houston"] },
      { name: "Great Lakes Cities", states: ["IL", "MI", "OH", "WI"], cities: ["Chicago", "Detroit", "Cleveland", "Columbus", "Milwaukee"] },
      { name: "Pacific Northwest Cities", states: ["WA", "OR"], cities: ["Seattle", "Tacoma", "Portland", "Salem"] },
    ],
  },
];

const Territories = ({ currentUserDoc, firebaseUser, teamUsers = [], onRefreshTeamUsers }) => {
  const app = getApp();
  const db = getFirestore(app);
  const auth = getAuth(app);

  const authUid = firebaseUser?.uid || auth.currentUser?.uid || null;

  const meFromTeamUsers = useMemo(() => {
    const uid = authUid;
    if (!uid) return null;
    return (teamUsers || []).find((u) => u?.id === uid || u?.uid === uid) || null;
  }, [teamUsers, authUid]);

  const me = useMemo(() => currentUserDoc || meFromTeamUsers || null, [currentUserDoc, meFromTeamUsers]);
  const org = useMemo(() => getOrgContext(me), [me]);

  const myRole = normalizeRole(me?.role);
  const canManage = myRole === "admin" || myRole === "owner";
  const isEndUserView = myRole === "enduser";

  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [packPickerOpen, setPackPickerOpen] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState(TERRITORY_PACKS[0]?.id || "");
  const [selectedPackTerritories, setSelectedPackTerritories] = useState([]);

  const [search, setSearch] = useState("");
  const [activeTerritoryId, setActiveTerritoryId] = useState(null);

  // Members UI
  const [userSearch, setUserSearch] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);

  // Territory detail editing (same fields as AccountsMap TerritoriesPanel)
  const [rightTab, setRightTab] = useState("members"); // "members" | "details"
  const [editName, setEditName] = useState("");
  const [statesInput, setStatesInput] = useState("");
  const [citiesInput, setCitiesInput] = useState("");
  const [zipInput, setZipInput] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const activeStaffUsers = useMemo(() => {
    return (teamUsers || [])
      .filter((u) => (u?.status || "Active").toString() !== "Deleted")
      .filter((u) => isStaffRole(u?.role))
      .sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b)));
  }, [teamUsers]);

  const territoriesById = useMemo(() => {
    const map = {};
    (territories || []).forEach((t) => {
      if (t?.id) map[t.id] = t;
    });
    return map;
  }, [territories]);

  const activeTerritory = useMemo(() => {
    if (!activeTerritoryId) return null;
    return territoriesById[activeTerritoryId] || null;
  }, [activeTerritoryId, territoriesById]);

  const activePack = useMemo(
    () => TERRITORY_PACKS.find((pack) => pack.id === selectedPackId) || TERRITORY_PACKS[0] || null,
    [selectedPackId]
  );

  const loadTerritories = useCallback(async () => {
    if (!org?.id) {
      setTerritories([]);
      setErr("Missing companyId on your profile. Cannot load territories.");
      return;
    }

    setLoading(true);
    setErr("");

    try {
      // Admin/Owner: load all
      if (canManage) {
        const snap = await getDocs(collection(db, org.root, org.id, "territories"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setTerritories(list);
        setActiveTerritoryId((prev) => (prev && list.some((t) => t.id === prev) ? prev : null));
        return;
      }

      // End user: best-effort load only assigned territories
      const myTerritoryIds = getUserTerritoryIds(me);
      if (myTerritoryIds.length === 0) {
        setTerritories([]);
        setActiveTerritoryId(null);
        return;
      }

      // Try full collection first; if denied, fall back to per-doc reads.
      try {
        const snap = await getDocs(collection(db, org.root, org.id, "territories"));
        const listAll = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const list = listAll
          .filter((t) => myTerritoryIds.includes(t.id))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setTerritories(list);
        setActiveTerritoryId((prev) => (prev && list.some((t) => t.id === prev) ? prev : null));
        return;
      } catch (e1) {
        if (e1?.code !== "permission-denied") throw e1;
        const reads = await Promise.all(
          myTerritoryIds.map(async (tid) => {
            const s = await getDoc(doc(db, org.root, org.id, "territories", tid));
            if (!s.exists()) return null;
            return { id: s.id, ...s.data() };
          })
        );
        const list = reads.filter(Boolean).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setTerritories(list);
        setActiveTerritoryId((prev) => (prev && list.some((t) => t.id === prev) ? prev : null));
      }
    } catch (e) {
      console.error("[Territories] loadTerritories error:", e);
      const code = e?.code ? ` (${e.code})` : "";
      setTerritories([]);
      setErr(`Failed to load territories${code}.`);
    } finally {
      setLoading(false);
    }
  }, [db, org, canManage, me]);

  useEffect(() => {
    loadTerritories();
  }, [loadTerritories]);

  // Member counts
  const memberCounts = useMemo(() => {
    const counts = {};
    for (const u of activeStaffUsers || []) {
      const tids = getUserTerritoryIds(u);
      for (const tid of tids) counts[tid] = (counts[tid] || 0) + 1;
    }
    return counts;
  }, [activeStaffUsers]);

  const filteredTerritories = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return territories;
    return (territories || []).filter((t) => (t?.name || "").toString().toLowerCase().includes(q));
  }, [territories, search]);

  // Admin create territory (now matches AccountsMap territory shape)
  const handleCreate = async () => {
    if (!canManage || !org?.id) return;
    const uid = authUid;
    if (!uid) return setErr("Not signed in yet. Try again in a moment.");

    const name = (newName || "").trim();
    if (!name) return;

    const normalized = name.toLowerCase();
    const exists = (territories || []).some((t) => (t?.name || "").toString().trim().toLowerCase() === normalized);
    if (exists) return setErr("That territory already exists.");

    setCreating(true);
    setErr("");

    try {
      const ref = await addDoc(collection(db, org.root, org.id, "territories"), {
        name,
        // match AccountsMap TerritoriesPanel
        states: [],
        cities: [],
        zipCodes: [],
        visibility: "all",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: uid,
      });

      setNewName("");
      await loadTerritories();
      setActiveTerritoryId(ref.id);
      setRightTab("members");
    } catch (e) {
      console.error("[Territories] create error:", e);
      const code = e?.code ? ` (${e.code})` : "";
      setErr(`Failed to create territory${code}.`);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenPackPicker = () => {
    setPackPickerOpen(true);
    const pack = TERRITORY_PACKS.find((item) => item.id === selectedPackId) || TERRITORY_PACKS[0] || null;
    setSelectedPackTerritories((pack?.territories || []).map((territory) => territory.name));
  };

  const handleApplyPack = async () => {
    if (!canManage || !org?.id || !activePack) return;

    const chosenTerritories = (activePack.territories || []).filter((territory) =>
      selectedPackTerritories.includes(territory.name)
    );
    if (!chosenTerritories.length) return;

    setCreating(true);
    setErr("");
    try {
      const existingNames = new Set(
        (territories || []).map((territory) => String(territory?.name || "").trim().toLowerCase())
      );
      const uid = authUid;

      for (const territory of chosenTerritories) {
        const normalizedName = String(territory.name || "").trim().toLowerCase();
        if (!normalizedName || existingNames.has(normalizedName)) continue;

        await addDoc(collection(db, org.root, org.id, "territories"), {
          name: territory.name,
          states: territory.states || [],
          cities: territory.cities || [],
          zipCodes: territory.zipCodes || [],
          visibility: "all",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
        });

        existingNames.add(normalizedName);
      }

      setPackPickerOpen(false);
      await loadTerritories();
    } catch (e) {
      console.error("[Territories] apply pack error:", e);
      const code = e?.code ? ` (${e.code})` : "";
      setErr(`Failed to apply territory pack${code}.`);
    } finally {
      setCreating(false);
    }
  };

  // -----------------------------
  // Admin territory members
  // -----------------------------
  const currentMemberIds = useMemo(() => {
    if (!activeTerritoryId) return [];
    return (activeStaffUsers || [])
      .filter((u) => getUserTerritoryIds(u).includes(activeTerritoryId))
      .map((u) => getUserId(u))
      .filter(Boolean);
  }, [activeTerritoryId, activeStaffUsers]);

  // Reset form state when switching territories
  useEffect(() => {
    if (!activeTerritoryId) {
      setSelectedUserIds([]);
      setUserSearch("");
      setShowSelectedOnly(false);

      setEditName("");
      setStatesInput("");
      setCitiesInput("");
      setZipInput("");
      setRightTab("members");
      return;
    }

    // members
    setSelectedUserIds(currentMemberIds);
    setUserSearch("");
    setShowSelectedOnly(false);

    // details
    const t = activeTerritory || {};
    setEditName(String(t?.name || ""));
    setStatesInput(Array.isArray(t?.states) ? t.states.join(", ") : "");
    setCitiesInput(Array.isArray(t?.cities) ? t.cities.join(", ") : "");
    setZipInput(Array.isArray(t?.zipCodes) ? t.zipCodes.join(", ") : "");
  }, [activeTerritoryId, currentMemberIds, activeTerritory]);

  const hasMemberChanges = useMemo(() => !setEquals(currentMemberIds, selectedUserIds), [currentMemberIds, selectedUserIds]);

  const toggleUser = (userId) => {
    if (!canManage) return;
    setSelectedUserIds((prev) => {
      const set = new Set(prev || []);
      if (set.has(userId)) set.delete(userId);
      else set.add(userId);
      return Array.from(set);
    });
  };

  const filteredUsersBase = useMemo(() => {
    const q = (userSearch || "").toLowerCase().trim();
    if (!q) return activeStaffUsers;

    return (activeStaffUsers || []).filter((u) => {
      const name = (u?.fullName || "").toString().toLowerCase();
      const email = (u?.email || "").toString().toLowerCase();
      const role = normalizeRole(u?.role);
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [activeStaffUsers, userSearch]);

  const filteredUsers = useMemo(() => {
    const base = filteredUsersBase || [];
    if (!showSelectedOnly) return base;
    const set = new Set((selectedUserIds || []).filter(Boolean));
    return base.filter((u) => set.has(getUserId(u)));
  }, [filteredUsersBase, showSelectedOnly, selectedUserIds]);

  const handleSaveMembers = async () => {
    if (!canManage || !org?.id || !activeTerritoryId) return;

    const uid = authUid;
    if (!uid) return setErr("Not signed in yet. Try again in a moment.");

    setSavingMembers(true);
    setErr("");

    try {
      const currentMembers = new Set(currentMemberIds);
      const desiredMembers = new Set((selectedUserIds || []).filter(Boolean));

      const updates = [];
      for (const id of desiredMembers) if (!currentMembers.has(id)) updates.push({ uid: id, action: "add" });
      for (const id of currentMembers) if (!desiredMembers.has(id)) updates.push({ uid: id, action: "remove" });

      if (updates.length === 0) return;

      // ✅ NEW: track newly assigned users (adds only) so we can notify them
      const newlyAssignedUserIds = updates
        .filter((u) => u.action === "add")
        .map((u) => u.uid)
        .filter(Boolean);

      const chunks = chunkArray(updates, 450);
      for (const chunk of chunks) {
        const batch = writeBatch(db);

        for (const u of chunk) {
          const user = (activeStaffUsers || []).find((x) => getUserId(x) === u.uid);
          if (!user) continue;

          const existing = getUserTerritoryIds(user);
          const set = new Set(existing);

          if (u.action === "add") set.add(activeTerritoryId);
          if (u.action === "remove") set.delete(activeTerritoryId);

          batch.update(doc(db, "users", u.uid), {
            territoryIds: Array.from(set),
            updatedAt: serverTimestamp(),
          });
        }

        await batch.commit();
      }

      // ✅ NEW: notify AFTER successful writes
      if (newlyAssignedUserIds.length) {
        const territoryName = (activeTerritory?.name || "Untitled territory").toString();

        console.log("[Territories] Notifying newly assigned territory users", {
          companyId: org.id,
          territoryId: activeTerritoryId,
          territoryName,
          newlyAssignedUserIds,
        });

        const results = await Promise.allSettled(
          newlyAssignedUserIds.map((assignedUserId) =>
            notifyTerritoryAssigned({
              companyId: org.id,
              territoryId: activeTerritoryId,
              territoryName,
              assignedUserId,
            })
          )
        );

        results.forEach((r, idx) => {
          if (r.status === "rejected") {
            console.error("[Territories] notifyTerritoryAssigned FAILED", {
              assignedUserId: newlyAssignedUserIds[idx],
              code: r.reason?.code,
              message: r.reason?.message,
              reason: r.reason,
            });
          }
        });
      }

      if (typeof onRefreshTeamUsers === "function") {
        await onRefreshTeamUsers();
      }
    } catch (e) {
      console.error("[Territories] save members error:", e);
      const code = e?.code ? ` (${e.code})` : "";
      setErr(`Failed to save members${code}.`);
    } finally {
      setSavingMembers(false);
    }
  };

  // -----------------------------
  // Admin territory details (same edit capability as AccountsMap TerritoriesPanel)
  // -----------------------------
  const parsedDetailPayload = useMemo(() => {
    return {
      name: (editName || "").trim(),
      states: parseStates(statesInput),
      cities: parseCities(citiesInput),
      zipCodes: parseZips(zipInput),
    };
  }, [editName, statesInput, citiesInput, zipInput]);

  const hasDetailChanges = useMemo(() => {
    const t = activeTerritory || {};
    const currentName = String(t?.name || "").trim();
    const currentStates = safeArray(t?.states).map((s) => String(s || "").trim().toUpperCase()).filter(Boolean);
    const currentCities = safeArray(t?.cities).map((c) => String(c || "").trim()).filter(Boolean);
    const currentZips = safeArray(t?.zipCodes).map((z) => norm5(z)).filter(Boolean);

    const nextName = parsedDetailPayload.name;
    const nextStates = parsedDetailPayload.states;
    const nextCities = parsedDetailPayload.cities;
    const nextZips = parsedDetailPayload.zipCodes;

    const sameName = currentName === nextName;
    const sameStates = setEquals(currentStates, nextStates);
    const sameZips = setEquals(currentZips, nextZips);

    // cities: compare case-insensitively
    const curCitySet = new Set(currentCities.map((c) => c.toLowerCase()));
    const nextCitySet = new Set(nextCities.map((c) => c.toLowerCase()));
    const sameCities = setEquals(Array.from(curCitySet), Array.from(nextCitySet));

    return !(sameName && sameStates && sameCities && sameZips);
  }, [activeTerritory, parsedDetailPayload]);

  const handleSaveDetails = async () => {
    if (!canManage || !org?.id || !activeTerritoryId) return;
    const uid = authUid;
    if (!uid) return setErr("Not signed in yet. Try again in a moment.");

    const name = parsedDetailPayload.name;
    if (!name) return setErr("Territory name is required.");

    // enforce unique names
    const normalized = name.toLowerCase();
    const exists = (territories || []).some(
      (t) => t.id !== activeTerritoryId && (t?.name || "").toString().trim().toLowerCase() === normalized
    );
    if (exists) return setErr("Another territory already has that name.");

    setSavingDetails(true);
    setErr("");

    try {
      const payload = {
        name,
        states: parsedDetailPayload.states,
        cities: parsedDetailPayload.cities,
        zipCodes: parsedDetailPayload.zipCodes,
        // keep this aligned with AccountsMap TerritoriesPanel default
        visibility: (activeTerritory?.visibility || "all") || "all",
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, org.root, org.id, "territories", activeTerritoryId), payload);

      // optimistic update
      setTerritories((prev) =>
        (prev || []).map((t) => (t.id === activeTerritoryId ? { ...t, ...payload } : t))
      );
    } catch (e) {
      console.error("[Territories] save details error:", e);
      const code = e?.code ? ` (${e.code})` : "";
      setErr(`Failed to save territory details${code}.`);
    } finally {
      setSavingDetails(false);
    }
  };

  const handleRevertDetails = () => {
    const t = activeTerritory || {};
    setEditName(String(t?.name || ""));
    setStatesInput(Array.isArray(t?.states) ? t.states.join(", ") : "");
    setCitiesInput(Array.isArray(t?.cities) ? t.cities.join(", ") : "");
    setZipInput(Array.isArray(t?.zipCodes) ? t.zipCodes.join(", ") : "");
  };

  const handleDeleteTerritory = async () => {
    if (!canManage || !org?.id || !activeTerritoryId) return;

    const tname = activeTerritory?.name || "this territory";
    const ok = window.confirm(
      `Delete "${tname}"?\n\nThis will also remove it from any users currently assigned.`
    );
    if (!ok) return;

    setDeleting(true);
    setErr("");

    try {
      // 1) delete territory doc
      await deleteDoc(doc(db, org.root, org.id, "territories", activeTerritoryId));

      // 2) cleanup assignments (remove territoryId from user territoryIds)
      const affected = (activeStaffUsers || [])
        .filter((u) => getUserTerritoryIds(u).includes(activeTerritoryId))
        .map((u) => getUserId(u))
        .filter(Boolean);

      const chunks = chunkArray(affected, 450);
      for (const c of chunks) {
        const batch = writeBatch(db);
        for (const uid of c) {
          const user = (activeStaffUsers || []).find((x) => getUserId(x) === uid);
          if (!user) continue;
          const existing = getUserTerritoryIds(user);
          const next = existing.filter((id) => id !== activeTerritoryId);
          batch.update(doc(db, "users", uid), {
            territoryIds: next,
            updatedAt: serverTimestamp(),
          });
        }
        await batch.commit();
      }

      if (typeof onRefreshTeamUsers === "function") {
        await onRefreshTeamUsers();
      }

      // 3) update local territories list and active selection
      setTerritories((prev) => (prev || []).filter((x) => x.id !== activeTerritoryId));
      setActiveTerritoryId((prev) => (prev === activeTerritoryId ? null : prev));
      setRightTab("members");
    } catch (e) {
      console.error("[Territories] delete territory error:", e);
      const code = e?.code ? ` (${e.code})` : "";
      setErr(`Failed to delete territory${code}.`);
    } finally {
      setDeleting(false);
    }
  };

  // -----------------------------
  // End-user view (read-only)
  // -----------------------------
  if (!canManage) {
    const myTerritories = (territories || []).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const myTerritoryNames = myTerritories.map((t) => t.name || t.id);

    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">View Only</h4>
              <p className="text-xs text-blue-700 mt-1">
                You can see the territories assigned to you. Only admins or owners can create, edit, or delete territories.
              </p>
            </div>
          </div>
        </div>

        {err && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            {err}
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPinned className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-900">
              {isEndUserView ? "My Territories" : "Territories"}
            </h2>
          </div>

          {loading ? (
            <div className="text-sm text-slate-600">Loading territories...</div>
          ) : myTerritoryNames.length === 0 ? (
            <div className="text-center py-6">
              <MapPinned className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">You are not assigned to any territories yet.</p>
              <p className="text-xs text-slate-500 mt-2">
                Assigned territories will appear here once an admin connects them to your profile.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {myTerritoryNames.map((name) => (
                <span
                  key={name}
                  className="px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-700"
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // -----------------------------
  // Admin view (manage)
  // -----------------------------
  const selectedCount = (selectedUserIds || []).length;

  const unsavedAny = hasMemberChanges || hasDetailChanges;
  const territoryActionButtons = activeTerritory ? (
    rightTab === "details" ? (
      <>
        <button
          type="button"
          onClick={handleRevertDetails}
          disabled={savingDetails || deleting || !hasDetailChanges}
          className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Revert
        </button>
        <button
          type="button"
          onClick={handleSaveDetails}
          disabled={savingDetails || deleting || !hasDetailChanges || !parsedDetailPayload.name}
          className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {savingDetails ? "Saving..." : "Save details"}
        </button>
      </>
    ) : (
      <button
        type="button"
        onClick={handleSaveMembers}
        disabled={savingMembers || !hasMemberChanges}
        className="inline-flex items-center justify-center rounded-xl bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
      >
        {savingMembers ? "Saving..." : "Save members"}
      </button>
    )
  ) : null;

  const territoryEditorContent = activeTerritory ? (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setRightTab("members")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs border ${
            rightTab === "members"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
          }`}
        >
          <UsersIcon className="w-3.5 h-3.5" />
          Members
          {hasMemberChanges && (
            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
              Unsaved
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setRightTab("details")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs border ${
            rightTab === "details"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          Details
          {hasDetailChanges && (
            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
              Unsaved
            </span>
          )}
        </button>

        {rightTab === "members" ? (
          <>
            <button
              type="button"
              onClick={() =>
                setSelectedUserIds(filteredUsersBase.map((u) => getUserId(u)).filter(Boolean))
              }
              disabled={savingMembers}
              className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={() => setSelectedUserIds([])}
              disabled={savingMembers}
              className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
            >
              Clear
            </button>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={showSelectedOnly}
                onChange={(e) => setShowSelectedOnly(e.target.checked)}
                className="w-3 h-3"
              />
              Selected only
            </label>
            <span className="text-xs text-slate-500">({selectedCount} selected)</span>
          </>
        ) : null}
      </div>

      {rightTab === "details" ? (
        <div className="space-y-3">
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Edit2 className="w-4 h-4 text-slate-400" />
              <div className="text-xs font-semibold text-slate-700">Edit full territory details</div>
              <div className="ml-auto text-[11px] text-slate-500 hidden md:block">
                Stored on{" "}
                <span className="font-mono">
                  companies/{org?.id}/territories/{activeTerritoryId}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <label className="text-[11px] text-slate-600">
                Name
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                  disabled={savingDetails || deleting}
                  placeholder="Territory name (e.g., Northeast)"
                />
              </label>

              <label className="text-[11px] text-slate-600">
                States (comma-separated: FL, GA, Virginia)
                <input
                  value={statesInput}
                  onChange={(e) => setStatesInput(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                  disabled={savingDetails || deleting}
                  placeholder="FL, GA, VA"
                />
              </label>

              <label className="text-[11px] text-slate-600">
                Cities (optional, comma-separated)
                <input
                  value={citiesInput}
                  onChange={(e) => setCitiesInput(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                  disabled={savingDetails || deleting}
                  placeholder="Miami, Atlanta"
                />
              </label>

              <label className="text-[11px] text-slate-600">
                ZIP codes (optional, comma-separated)
                <input
                  value={zipInput}
                  onChange={(e) => setZipInput(e.target.value)}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                  disabled={savingDetails || deleting}
                  placeholder="33101, 30301"
                />
              </label>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleDeleteTerritory}
                disabled={savingDetails || deleting || savingMembers}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                title="Delete territory"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>

            <div className="text-[11px] text-slate-500">
              Preview:{" "}
              <span className="font-mono">
                states=[{parsedDetailPayload.states.join(", ")}]{" "}
                cities=[{parsedDetailPayload.cities.join(", ")}]{" "}
                zipCodes=[{parsedDetailPayload.zipCodes.join(", ")}]
              </span>
            </div>
          </div>

          <div className="text-[11px] text-slate-500">
            Tip: accounts can match by <span className="font-semibold">state</span>,{" "}
            <span className="font-semibold">city</span>, or <span className="font-semibold">ZIP</span>{" "}
            (same logic used in your Account Map territory filters).
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search staff..."
              className="w-full pl-7 pr-2 py-1 border border-slate-300 rounded text-xs"
            />
          </div>

          <div className="border border-slate-200 rounded max-h-80 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500">No users found</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredUsers.map((u) => {
                  const uid = getUserId(u);
                  if (!uid) return null;
                  const checked = selectedUserIds.includes(uid);
                  const role = normalizeRole(u?.role);

                  return (
                    <label
                      key={uid}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleUser(uid)}
                        className="w-3.5 h-3.5"
                      />
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center">
                        {getInitials(u.fullName, u.email)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-900 truncate">
                          {u.fullName || "Unnamed"}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
                        {role}
                      </span>
                      {checked && <CheckCircle className="w-3.5 h-3.5 text-green-600" />}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="text-[11px] text-slate-500">
            Member assignments are stored on user profiles as{" "}
            <span className="font-mono">territoryIds: []</span>.
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-4 territories-shell">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <MapPinned className="h-4 w-4 text-slate-600" />
              <h2 className="text-sm font-semibold text-slate-900">Territories</h2>
              {unsavedAny ? (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  Unsaved
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Organize reps and accounts into cleaner foodservice regions, then assign staff where it makes sense.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleOpenPackPicker}
              className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Territory packs
            </button>
            <button
              type="button"
              onClick={loadTerritories}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-xl text-xs">
          {err}
        </div>
      )}

      {packPickerOpen && activePack ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Territory Packs
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Pick a preset and multi-select the regions you want to add.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPackPickerOpen(false)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-[240px_minmax(0,1fr)]">
            <div className="space-y-2">
              <select
                value={selectedPackId}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedPackId(nextId);
                  const nextPack = TERRITORY_PACKS.find((item) => item.id === nextId);
                  setSelectedPackTerritories((nextPack?.territories || []).map((territory) => territory.name));
                }}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
              >
                {TERRITORY_PACKS.map((pack) => (
                  <option key={pack.id} value={pack.id}>
                    {pack.name}
                  </option>
                ))}
              </select>
              <p className="text-xs leading-5 text-slate-500">{activePack.description}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Territories in this pack
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedPackTerritories((activePack.territories || []).map((territory) => territory.name))
                  }
                  className="text-[11px] font-medium text-slate-600 hover:text-slate-900"
                >
                  Select all
                </button>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {(activePack.territories || []).map((territory) => {
                  const checked = selectedPackTerritories.includes(territory.name);
                  return (
                    <label
                      key={territory.name}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-xl border px-3 py-1 ${
                        checked ? "border-blue-200 bg-blue-50/60" : "border-slate-200 bg-slate-50/70"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedPackTerritories((prev) =>
                            prev.includes(territory.name)
                              ? prev.filter((name) => name !== territory.name)
                              : [...prev, territory.name]
                          )
                        }
                        className="mt-0.5 h-4 w-4 rounded border-slate-300"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium leading-5 text-slate-900">{territory.name}</div>
                        <div className="mt-0.5 text-[11px] leading-4 text-slate-500">
                          {territory.cities?.length
                            ? `Cities: ${territory.cities.join(", ")}`
                            : territory.states?.length
                            ? `States: ${territory.states.join(", ")}`
                            : "Regional shell"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleApplyPack}
                  disabled={creating || selectedPackTerritories.length === 0}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  <CheckSquare2 className="h-4 w-4" />
                  {creating ? "Adding..." : "Add selected territories"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Body */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left */}
        <div className="col-span-12 rounded-2xl border border-slate-200 bg-slate-50 p-3 md:col-span-4">
          <div className="flex gap-2 mb-3">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New territory name"
              className="flex-1 border border-slate-300 rounded-xl px-3 py-2 text-xs bg-white"
              disabled={creating}
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-3 text-white hover:bg-blue-700 disabled:opacity-50"
              title="Create territory"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search territories..."
              className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-xl text-xs bg-white"
            />
          </div>

          <div className="space-y-0.5 max-h-[34rem] overflow-y-auto pr-1">
            {loading ? (
              <div className="p-3 text-xs text-slate-500 text-center">Loading...</div>
            ) : filteredTerritories.length === 0 ? (
              <div className="p-3 text-xs text-slate-500 text-center">No territories</div>
            ) : (
              filteredTerritories.map((t) => {
                const isActive = t.id === activeTerritoryId;
                const count = memberCounts[t.id] || 0;

                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTerritoryId(t.id)}
                    className={`w-full rounded-xl border px-3 py-1 text-left transition-colors ${
                      isActive
                        ? "border-blue-200 bg-white shadow-sm"
                        : "border-transparent hover:border-slate-200 hover:bg-white/70"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex-1 text-xs font-medium text-slate-900 truncate">
                        {t.name || "Untitled"}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-mono text-slate-500">
                        {count}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {Array.isArray(t.states) && t.states.length > 0 ? (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600">
                          {t.states.length} states
                        </span>
                      ) : null}
                      {Array.isArray(t.cities) && t.cities.length > 0 ? (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600">
                          {t.cities.length} cities
                        </span>
                      ) : null}
                      {Array.isArray(t.zipCodes) && t.zipCodes.length > 0 ? (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600">
                          {t.zipCodes.length} ZIPs
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right */}
        <div className="hidden md:block md:col-span-8 rounded-2xl border border-slate-200 bg-white p-3">
          {!activeTerritory ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <MapPinned className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-600">Select a territory</p>
                <p className="text-xs text-slate-400 mt-1">Select a territory to highlight its region here and edit full territory details.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Top header */}
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="text-sm font-semibold text-slate-900 truncate">
                    {activeTerritory.name || "Untitled territory"}
                  </h3>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    • {memberCounts[activeTerritoryId] || 0} members
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {territoryActionButtons}
                </div>
              </div>
              {territoryEditorContent}
            </div>
          )}
        </div>
      </div>

      {isMobile && activeTerritory ? (
        <div className="fixed inset-0 z-50 bg-white md:hidden">
          <div className="flex h-full flex-col overflow-hidden">
            <div className="sticky top-0 z-20 border-b border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setActiveTerritoryId(null)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                >
                  <X className="h-4 w-4" />
                  Close
                </button>
                <div className="min-w-0 text-right">
                  <div className="truncate text-sm font-semibold text-slate-900">
                    {activeTerritory.name || "Untitled territory"}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {memberCounts[activeTerritoryId] || 0} members
                  </div>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
              {territoryEditorContent}
            </div>

            <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center justify-end gap-2">
                {territoryActionButtons}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Territories;
